import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import useSWR from "swr";

import DebouncedInput from "../../components/DebouncedInput";
import { CountrySelectSimple } from "../../components/form-fields/CountrySelect";
import { RegionSelectSimple } from "../../components/form-fields/RegionSelect";
import useSearchState from "../../components/hooks/useSearchState";
import MemberItem from "../../components/members/MemberItem";
import PageLoadingMessage from "../../components/PageLoadingMessage";

const getPaginationUrl = (url, history) => {
  if (!url) return history.location.pathname + history.location.search;

  const parsedUrl = new URL(url);
  const search = new URLSearchParams(parsedUrl.search);
  search.delete("is_organization_user");
  return history.location.pathname + "?" + search.toString();
};

const MembersIndex = () => {
  const router = useRouter();
  const search = new URLSearchParams(router.query);
  search.set("is_organization_user", "false");
  const queryKey = "/api/users/?" + search.toString();

  const { data: members = {}, error } = useSWR(queryKey);

  const [showFilterOptions, setShowFilterOptions] = useState(
    !!Object.keys(router.query).length
  );

  // TODO: remove since only used for pagination url generation
  const history = {
    location: {
      pathname: router.pathname,
    },
  };

  const isFirstLoading = !members.results && !error;

  const [selectedCountry, setSelectedCountry] = useSearchState("country", {
    resetDependentKeys: ["region", "page"],
  });
  const [selectedRegion, setSelectedRegion] = useSearchState("region", {
    resetPageNumber: true,
  });
  const [searchString, setSearchString] = useSearchState("search", {
    resetPageNumber: true,
  });

  return (
    <div className="members-list">
      <section className="hero is-primary">
        <div className="hero-body">
          <div className="container is-max-desktop">
            <h1 className="title">Search Members</h1>
          </div>
        </div>
      </section>

      <section className="section members-search">
        <div className="container is-max-desktop">
          <div className="field is-grouped">
            <div className="control is-expanded">
              <DebouncedInput
                value={searchString}
                delay={300}
                onChange={setSearchString}
                placeholder="search for members..."
              />
            </div>

            <div className="control">
              <button
                className="button is-info"
                onClick={() => setShowFilterOptions(!showFilterOptions)}
              >
                Filter Options
              </button>
            </div>
          </div>

          <div
            className={
              "columns is-multiline mt-2 " +
              (showFilterOptions ? "" : "is-hidden")
            }
          >
            <div className="column is-half mb-0 field">
              <div className="control is-expanded">
                <CountrySelectSimple
                  value={selectedCountry}
                  onChange={setSelectedCountry}
                  placeholder="Filter by country"
                />
              </div>
            </div>

            <div className="column is-half mb-0 field">
              <div className="control is-expanded">
                <RegionSelectSimple
                  country={selectedCountry}
                  placeholder="Filter by region"
                  value={selectedRegion}
                  onChange={setSelectedRegion}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container is-max-desktop">
          {isFirstLoading && <PageLoadingMessage />}

          {members.results?.length > 0 && (
            <div className="columns">
              <div className="column">
                {members.results
                  ?.filter((_, idx) => idx % 3 === 0)
                  .map((member) => (
                    <MemberItem key={member.username} member={member} />
                  ))}
              </div>
              <div className="column">
                {members.results
                  ?.filter((_, idx) => idx % 3 === 1)
                  .map((member) => (
                    <MemberItem key={member.username} member={member} />
                  ))}
              </div>
              <div className="column">
                {members.results
                  ?.filter((_, idx) => idx % 3 === 2)
                  .map((member) => (
                    <MemberItem key={member.username} member={member} />
                  ))}
              </div>
            </div>
          )}

          {members.results?.length === 0 && (
            <h2 className="subtitle">No members found...</h2>
          )}
        </div>
      </section>

      <section className="section">
        <nav className="pagination" role="navigation" aria-label="pagination">
          <Link
            href={getPaginationUrl(members.previous, history)}
            className="pagination-previous"
            disabled={!members.previous}
          >
            Prev
          </Link>
          <Link
            href={getPaginationUrl(members.next, history)}
            className="pagination-next"
            disabled={!members.next}
          >
            Next
          </Link>
          <ul className="pagination-list">
            {/* ul is there so the pagination is weighted to the right */}
          </ul>
        </nav>
      </section>
    </div>
  );
};

export default MembersIndex;
